import { cn } from '@/lib/utils'
import { AlertTriangle, type LucideIcon } from 'lucide-react'

interface StatsCardProps {
  title: string
  value: string | number
  change?: {
    value: number
    trend: 'up' | 'down' | 'neutral'
    suffix?: string
    label?: string
    colorOverride?: string
  }
  icon: LucideIcon
  warning?: string
  subtitle?: string
  variant?: 'default' | 'hero'
  loading?: boolean
  className?: string
  onClick?: () => void
  badge?: React.ReactNode
}

export function StatsCard({
  title,
  value,
  change,
  icon: Icon,
  warning,
  subtitle,
  variant = 'default',
  loading = false,
  className,
  onClick,
  badge,
}: StatsCardProps) {
  if (loading) {
    return (
      <div className={cn('rounded-lg border bg-card p-4 sm:p-6 shadow-sm animate-pulse', className)}>
        <div className="flex items-center justify-between">
          <div className="h-4 w-24 bg-muted rounded" />
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-muted" />
        </div>
        <div className="mt-2 sm:mt-4">
          <div className="h-8 w-32 bg-muted rounded" />
        </div>
      </div>
    )
  }

  const isHero = variant === 'hero'

  return (
    <div
      onClick={onClick}
      className={cn(
        'rounded-lg border bg-card p-4 sm:p-6 shadow-sm',
        isHero && 'border-l-4 border-l-brand-fg',
        onClick && 'cursor-pointer hover:border-brand-fg/50 transition-colors',
        className
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
          <p className="text-xs sm:text-sm font-medium text-muted-foreground">{title}</p>
          {badge}
        </div>
        <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-brand-fg/10 shrink-0">
          <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-brand-fg" />
        </div>
      </div>
      <div className="mt-2 sm:mt-4">
        <p className={cn('font-bold truncate', isHero ? 'text-2xl sm:text-4xl' : 'text-xl sm:text-3xl')}>
          {value}
        </p>
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
        )}
        {warning && (
          <div className="mt-1 flex items-center gap-1" title={warning}>
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
            <span className="text-xs text-amber-500 truncate">{warning}</span>
          </div>
        )}
        {change && change.value !== 0 && (
          <div className="mt-1 flex items-center gap-1 flex-wrap">
            <span
              className={cn(
                'text-xs sm:text-sm font-medium',
                change.colorOverride
                  ? undefined
                  : change.trend === 'up'
                    ? 'text-green-500'
                    : change.trend === 'down'
                      ? 'text-red-500'
                      : 'text-muted-foreground'
              )}
              style={change.colorOverride ? { color: change.colorOverride } : undefined}
            >
              {change.value > 0 ? '+' : ''}{change.value}{change.suffix || ''}
            </span>
            <span className="text-xs sm:text-sm text-muted-foreground">
              {change.label || 'in last 24h'}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
